import React, { useState } from "react";
import FormInput from "../../components/common/FormInput";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { LoaderButton } from "../../components/common/LoadingButton";
import {
  SignupFormInitialValues,
  SignupFormValidationSchema,
} from "../../utils/validation";
import { SignupFormValues, SignupPayload } from "types/signup";
import { useAuth } from "hooks/useAuth";
import { motion } from "framer-motion";
import { Button } from "@headlessui/react";
import { AuthStatus } from "types/auth";
import { useGoogleLogin } from "@react-oauth/google";
import { Link } from "react-router-dom";

export default function Signup() {
  const { userSignup } = useAuth();
  const [authStatus, setAuthStatus] = useState<AuthStatus>("idle");
  const { loginWithGoogle } = useAuth();

  const handleGoogleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      try {
        setAuthStatus("loading");
        await loginWithGoogle(code);
        setAuthStatus("success");
      } catch (error) {
        setAuthStatus("error");
      }
    },
    onError: () => {
      setAuthStatus("error");
    },
  });

  const handelSubmit = async (
    values: SignupFormValues,
    actions: FormikHelpers<SignupFormValues>,
  ) => {
    const payload: SignupPayload = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      password: values.password,
    };
    await userSignup(payload);

    actions.resetForm();
  };

  return (
  <div className="bg-primary rounded-2xl border shadow-lg border-[#222735] w-full pt-7 pb-8 px-4 overflow-y-auto">
    
    <div className="text-center">
      <motion.img
        src="/assets/worklap-white-logo.svg"
        className="mx-auto w-10"
        alt="Worklap Logo"
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />
      <h1 className="font-semibold text-xl md:text-2xl w-full mt-[12px]">
        Get Started
      </h1>
      <p className="text-sm text-neutral-medium pt-[5px]">
        Create an account to start managing your workforce
      </p>
    </div>

    <Formik<SignupFormValues>
      initialValues={SignupFormInitialValues}
      validationSchema={SignupFormValidationSchema}
      onSubmit={handelSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, dirty, isSubmitting }: FormikProps<SignupFormValues>) => (
        <Form onSubmit={handleSubmit}>
          <div className="w-full max-w-[400px] mx-auto pt-4 space-y-3">
            <div className="flex flex-row gap-4">
 <FormInput name="firstName" type="text" value={values.firstName} onChange={handleChange} onBlur={handleBlur} error={touched.firstName && errors.firstName} placeholder="First Name" darkBg whiteLabel />
            <FormInput name="lastName" type="text" value={values.lastName} onChange={handleChange} onBlur={handleBlur} error={touched.lastName && errors.lastName} placeholder="Last Name" darkBg whiteLabel />
            </div>
           
            <FormInput name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && errors.email} placeholder="Email" darkBg whiteLabel />
            <FormInput name="password" type="password" value={values.password} onChange={handleChange} onBlur={handleBlur} error={touched.password && errors.password} placeholder="Create Password" darkBg whiteLabel />
            <FormInput name="confirmPassword" type="password" value={values.confirmPassword} onChange={handleChange} onBlur={handleBlur} error={touched.confirmPassword && errors.confirmPassword} placeholder="Confirm Password" darkBg whiteLabel />
            <LoaderButton disabled={!isValid || !dirty || isSubmitting} className="!w-full" text="Sign Up" />
          </div>
        </Form>
      )}
    </Formik>

    {/* Divider */}
    <div className="flex items-center my-6 w-full max-w-[400px] mx-auto">
      <div className="flex-grow border-t border-[#374151]" />
      <span className="mx-4 text-sm text-gray-400 font-medium">or</span>
      <div className="flex-grow border-t border-[#374151]" />
    </div>

    {/* Google button */}
    <div className="w-full max-w-[400px] mx-auto">
      <Button
        className="bg-[#181C27] border border-[#232941] h-[46px] rounded-lg flex justify-center gap-3 items-center w-full"
        onClick={handleGoogleLogin}
        disabled={authStatus === "loading"}
      >
        <img src="/assets/google-icon.svg" className="w-4" alt="Google Logo" />
        {authStatus === "loading" ? "Signing in..." : "Sign up with Google"}
      </Button>
    </div>

    <p className="text-center text-sm text-gray-400 mt-6">
      Already have an account?{" "}
      <Link to="/login" className="text-[#6366F1] hover:underline">
        Sign in
      </Link>
    </p>

  </div>
);
}
